import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { Modal } from "../../../components/dashboard/Modal";
import { Pagination } from "../../../components/dashboard/Pagination";
import { PlaylistAssignmentEditor } from "../../../components/dashboard/PlaylistAssignmentEditor";
import { useConfirm } from "../../../contexts/ConfirmContext";
import { useToast } from "../../../contexts/ToastContext";
import { extractErrorMessage } from "../../../lib/api";
import {
  claimScreen,
  deleteScreen,
  generateClaimCode,
  getScreenAssignments,
  getScreenStats,
  listClients,
  listPlaylists,
  listScreens,
  rebootScreen,
  setScreenAssignments,
  updateScreen,
} from "../../../lib/dashboardApi";
import { timeAgo } from "../../../lib/format";
import { hasMinRole } from "../../../lib/hooks/useAuthMe";
import type {
  PlaylistAssignment,
  Screen,
  User,
} from "../../../types/dashboard";

type Ctx = { user?: User };

export function Screens() {
  const { user } = useOutletContext<Ctx>();
  const canAdmin = hasMinRole(user, "admin");
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [status, setStatus] = useState("all");
  const [claim, setClaim] = useState("all");
  const [clientId, setClientId] = useState("");
  const [editing, setEditing] = useState<Screen | null>(null);
  const [claimCode, setClaimCode] = useState<{ name: string; code: string } | null>(null);

  const { data: stats } = useQuery({ queryKey: ["screen-stats"], queryFn: getScreenStats });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: listClients });
  const { data: playlists = [] } = useQuery({
    queryKey: ["playlists"],
    queryFn: listPlaylists,
  });
  const { data: screensData, isLoading } = useQuery({
    queryKey: ["screens", { page, status, claim, clientId, search }],
    queryFn: () =>
      listScreens({
        page,
        per_page: 50,
        status,
        claim,
        client_id: clientId || undefined,
        search: search || undefined,
      }),
  });
  const screens = screensData?.screens ?? [];

  async function handleClaim(s: Screen) {
    try {
      await claimScreen(s.id);
      qc.invalidateQueries({ queryKey: ["screens"] });
      qc.invalidateQueries({ queryKey: ["screen-stats"] });
      toast("Screen claimed.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not claim."), "error");
    }
  }

  async function genCode(s: Screen) {
    try {
      const r = await generateClaimCode(s.id);
      setClaimCode({ name: s.name, code: r.code });
    } catch (e) {
      toast(extractErrorMessage(e, "Could not generate code."), "error");
    }
  }

  async function remove(s: Screen) {
    if (
      !(await confirm({
        title: "Delete screen?",
        message: `Delete ${s.name}? The Pi will be unclaimed.`,
        danger: true,
      }))
    )
      return;
    try {
      await deleteScreen(s.id);
      qc.invalidateQueries({ queryKey: ["screens"] });
      qc.invalidateQueries({ queryKey: ["screen-stats"] });
      toast("Deleted.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not delete."), "error");
    }
  }

  async function reboot(s: Screen) {
    if (
      !(await confirm({
        title: "Reboot Pi?",
        message: `Reboot ${s.name}? The screen will go dark for a minute or two while it restarts.`,
        danger: true,
      }))
    )
      return;
    try {
      await rebootScreen(s.id);
      toast(`Reboot sent to ${s.name}.`);
    } catch (e) {
      toast(extractErrorMessage(e, "Could not reboot."), "error");
    }
  }

  function copyCode() {
    if (!claimCode) return;
    navigator.clipboard.writeText(claimCode.code).then(
      () => toast("Copied."),
      () => toast("Copy failed.", "error"),
    );
  }

  return (
    <>
      <h2 className="page-title">Pi Screens</h2>

      <div className="stats-row">
        <StatCard label="Total Pi Screens" value={stats?.total ?? 0} />
        <StatCard label="Online" value={stats?.online ?? 0} color="var(--green)" />
        <StatCard label="Offline" value={stats?.offline ?? 0} color="var(--red)" />
        {stats?.unclaimed ? (
          <StatCard label="Unclaimed" value={stats.unclaimed} color="var(--orange)" />
        ) : null}
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Registered Displays</h3>
        </div>
        <div className="sort-bar">
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => {
              if (searchOpen) setSearch("");
              setSearchOpen(!searchOpen);
            }}
          >
            {searchOpen ? "Close" : "Search"}
          </button>
          {searchOpen && (
            <input
              type="text"
              placeholder="Type to search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              autoFocus
            />
          )}
          <label>Status:</label>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>
          <label>Claim:</label>
          <select
            value={claim}
            onChange={(e) => {
              setClaim(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="unclaimed">Unclaimed</option>
            <option value="claimed">Claimed</option>
          </select>
          <label>Group:</label>
          <select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Groups</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {isLoading ? (
          <div className="empty">Loading…</div>
        ) : screens.length === 0 ? (
          <div className="empty">No screens found.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name / Player ID</th>
                <th>Status</th>
                <th>Sync</th>
                <th>Claim</th>
                <th>Group</th>
                <th>IP / Hostname</th>
                <th>Last seen</th>
                <th>Playlist</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {screens.map((s) => {
                const playlist = playlists.find((p) => p.id === s.playlist_id);
                const cl = clients.find((c) => c.id === s.client_id);
                return (
                  <tr key={s.id}>
                    <td>
                      <strong>{s.name}</strong>
                      <br />
                      <span style={{ fontFamily: "monospace", fontSize: 10, color: "var(--text2)" }}>
                        {s.player_id ?? s.device_id.slice(0, 12) + "..."}
                      </span>
                      {s.app_version && (
                        <>
                          <br />
                          <span style={{ fontSize: 9, color: "var(--text2)" }}>
                            v{s.app_version}
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      <span className={s.is_online ? "badge badge-online" : "badge badge-offline"}>
                        {s.is_online ? "Online" : "Offline"}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <SyncDot status={s.sync_status} />
                    </td>
                    <td>
                      <span
                        className={
                          s.claim_status === "claimed" ? "badge badge-online" : "badge badge-offline"
                        }
                      >
                        {s.claim_status === "claimed" ? "Claimed" : "Unclaimed"}
                      </span>
                      {canAdmin && s.claim_status !== "claimed" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          style={{ marginLeft: 4, padding: "3px 8px", fontSize: 11 }}
                          onClick={() => handleClaim(s)}
                        >
                          Claim
                        </button>
                      )}
                      {canAdmin && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline"
                          style={{ marginLeft: 4, padding: "3px 8px", fontSize: 11 }}
                          onClick={() => genCode(s)}
                        >
                          Code
                        </button>
                      )}
                    </td>
                    <td>
                      {cl?.name ?? <span style={{ color: "var(--text2)" }}>—</span>}
                    </td>
                    <td>
                      <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                        {s.ip_address ?? "-"}
                        {s.hostname ? ` / ${s.hostname}` : ""}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: "var(--text2)", fontSize: 12 }}>
                        {timeAgo(s.last_seen)}
                      </span>
                    </td>
                    <td>
                      {playlist?.name ?? <span style={{ color: "var(--text2)" }}>-</span>}
                    </td>
                    <td>
                      {canAdmin && (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => setEditing(s)}
                          >
                            Edit
                          </button>{" "}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => reboot(s)}
                            disabled={!s.is_online}
                            title={s.is_online ? "Reboot this Pi" : "Pi is offline"}
                            aria-label={`Reboot ${s.name}`}
                          >
                            Reboot
                          </button>{" "}
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => remove(s)}
                            aria-label="Delete screen"
                          >
                            ×
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination
          page={screensData?.page ?? 1}
          pages={screensData?.pages ?? 1}
          total={screensData?.total ?? 0}
          itemLabel="screen"
          onChange={setPage}
        />
      </div>

      {editing && (
        <ScreenEditModal
          screen={editing}
          clients={clients}
          playlists={playlists}
          onClose={() => setEditing(null)}
        />
      )}

      <Modal
        open={!!claimCode}
        onClose={() => setClaimCode(null)}
        width={420}
        ariaLabel="Claim code"
      >
        {claimCode && (
          <div style={{ textAlign: "center" }}>
            <h3 style={{ marginBottom: 8 }}>Claim Code</h3>
            <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20 }}>
              {claimCode.name}
            </p>
            <div
              style={{
                background: "var(--surface2)",
                border: "2px solid var(--accent)",
                borderRadius: 12,
                padding: "24px 16px",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 42,
                  fontWeight: 700,
                  fontFamily: "monospace",
                  letterSpacing: 12,
                  color: "var(--accent)",
                }}
              >
                {claimCode.code}
              </div>
            </div>
            <p style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>
              Enter this code on the Pi player setup screen (press <kbd>i</kbd> to open).
            </p>
            <p style={{ fontSize: 12, color: "var(--text2)", marginBottom: 16 }}>
              Expires in 24 hours.
            </p>
            <div className="modal-actions" style={{ justifyContent: "center" }}>
              <button type="button" className="btn btn-outline" onClick={copyCode}>
                Copy Code
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setClaimCode(null)}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color?: string;
}) {
  return (
    <div className="stat-card">
      <div className="stat-val" style={color ? { color } : undefined}>
        {value}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function SyncDot({ status }: { status?: Screen["sync_status"] }) {
  const map: Record<string, { color: string; title: string }> = {
    synced: { color: "var(--green)", title: "Synced" },
    syncing: { color: "var(--orange)", title: "Syncing" },
    out_of_sync: { color: "var(--red)", title: "Out of sync" },
  };
  const config = status && map[status];
  if (!config) return <span style={{ color: "var(--text2)" }}>—</span>;
  return (
    <span
      style={{ color: config.color, fontSize: 18 }}
      title={config.title}
      aria-label={config.title}
    >
      ●
    </span>
  );
}

function ScreenEditModal({
  screen,
  clients,
  playlists,
  onClose,
}: {
  screen: Screen;
  clients: { id: number; name: string }[];
  playlists: { id: number; name: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: screen.name,
    location_name: screen.location_name ?? "",
    client_id: screen.client_id ?? 0,
    rotation: screen.rotation ?? 0,
    fit_mode: screen.fit_mode ?? "contain",
  });
  const [assignments, setAssignments] = useState<PlaylistAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getScreenAssignments(screen.id)
      .then((list) => {
        if (!cancelled) {
          setAssignments(list);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAssignments([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [screen.id]);

  async function save() {
    setSaving(true);
    try {
      await updateScreen(screen.id, {
        name: form.name,
        location_name: form.location_name,
        client_id: form.client_id === 0 ? null : form.client_id,
        rotation: form.rotation,
        fit_mode: form.fit_mode,
      });
      await setScreenAssignments(screen.id, assignments);
      qc.invalidateQueries({ queryKey: ["screens"] });
      toast("Screen updated.");
      onClose();
    } catch (e) {
      toast(extractErrorMessage(e, "Could not save."), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} width={640} ariaLabel="Edit screen">
      <h3>Edit Screen</h3>
      <div className="form-group">
        <label>Screen Group</label>
        <select
          className="form-input"
          value={form.client_id}
          onChange={(e) => setForm({ ...form, client_id: Number(e.target.value) })}
        >
          <option value={0}>No group</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Screen Name</label>
        <input
          className="form-input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>
      <div className="form-group">
        <label>Location name (for reports)</label>
        <input
          className="form-input"
          value={form.location_name}
          onChange={(e) => setForm({ ...form, location_name: e.target.value })}
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Screen Rotation</label>
          <select
            className="form-input"
            value={form.rotation}
            onChange={(e) => setForm({ ...form, rotation: Number(e.target.value) })}
          >
            <option value={0}>0° — Landscape</option>
            <option value={90}>90° — Portrait (Right)</option>
            <option value={180}>180° — Landscape (Inverted)</option>
            <option value={270}>270° — Portrait (Left)</option>
          </select>
        </div>
        <div className="form-group">
          <label>Fit mode</label>
          <select
            className="form-input"
            value={form.fit_mode}
            onChange={(e) => setForm({ ...form, fit_mode: e.target.value })}
          >
            <option value="stretch">Stretch</option>
            <option value="fill">Fill</option>
            <option value="contain">Contain</option>
            <option value="center">Center</option>
          </select>
        </div>
      </div>
      <div className="form-group">
        <label>Assigned Playlists</label>
        {loading ? (
          <div style={{ fontSize: 13, color: "var(--text2)" }}>Loading…</div>
        ) : (
          <PlaylistAssignmentEditor
            assignments={assignments}
            playlists={playlists as never}
            onChange={setAssignments}
          />
        )}
      </div>
      <div className="modal-actions">
        <button type="button" className="btn btn-outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}
